import React, { useEffect, useMemo, useState } from 'react';

import { Container, Typography } from '@material-ui/core';
import { Add as PlusIcon } from '@material-ui/icons';
import { isPast } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { generatePath, useHistory } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import FooterButton from 'components/FooterButton';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import dsarApi from 'config/api/dsar/dsar';
import type { DsarCase, DsarDomainValueSummary } from 'config/api/dsar/dsar.types';
import DIRECTIONS from 'config/constants/sortDirections';
import dsar_messages from 'messages/dsar_messages';
import general_messages from 'messages/general_messages';
import { useHelpDialogContext } from 'reactContext/HelpDialogContext/HelpDialogContext';
import PATHS from 'router/PATHS';

import DsarCaseCreateDialog from './_components/DsarCaseCreateDialog';
import DsarCasesTable from './_components/DsarCasesTable';
import useStyles from './DsarCasesPage.styles';

const pageSize = 20;
const REQUEST_TYPE_GROUP = 'request_type';
const CASE_STATUS_GROUP = 'case_status';
const IDENTITY_STATUS_GROUP = 'identity_verification_status';
const PAST_DUE_DATE_FILTER_ID = 'past_due_date';

const mapFilterItems = (items: { id: string; name: string }[]) =>
  items.sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }));

const isCaseOverdue = (dsarCase: DsarCase) =>
  Boolean(dsarCase.dueDate && isPast(new Date(dsarCase.dueDate)) && !['completed', 'closed'].includes(dsarCase.status.code));

const getSearchText = (dsarCase: DsarCase) =>
  [
    dsarCase.caseReference,
    dsarCase.requesterName,
    dsarCase.requesterEmail,
    dsarCase.status.name,
    dsarCase.identityVerificationStatus.name,
    ...dsarCase.requestTypes.map(({ requestType }) => requestType.name),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const getSortValue = (dsarCase: DsarCase, key: string | null) => {
  switch (key) {
    case 'caseReference':
      return dsarCase.caseReference;
    case 'requesterName':
      return dsarCase.requesterName;
    case 'status':
      return dsarCase.status.name;
    case 'identityVerificationStatus':
      return dsarCase.identityVerificationStatus.name;
    case 'receivedAt':
      return dsarCase.receivedAt;
    case 'dueDate':
      return dsarCase.dueDate;
    case 'distributedAt':
      return dsarCase.distributedAt || '';
    case 'createdAt':
      return dsarCase.createdAt;
    default:
      return '';
  }
};

const DsarCasesPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const { showHelpDialog } = useHelpDialogContext();
  const [currentOffset, setCurrentOffset] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState<{ key: string | null; direction: string | null }>({
    key: null,
    direction: null,
  });
  const [loadedCases, setLoadedCases] = useState<DsarCase[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filters, setFilters] = useState<Record<string, Array<{ id: string; name: string }>>>({});

  const { data: requestTypeValuesResponse, isLoading: requestTypeValuesLoading } = useQuery(
    ['DSAR_DOMAIN_VALUES', REQUEST_TYPE_GROUP],
    () => dsarApi.getDomainValues({ group: REQUEST_TYPE_GROUP }),
    { retry: false, staleTime: 5 * 60 * 1000 },
  );
  const { data: caseStatusValuesResponse, isLoading: caseStatusValuesLoading } = useQuery(
    ['DSAR_DOMAIN_VALUES', CASE_STATUS_GROUP],
    () => dsarApi.getDomainValues({ group: CASE_STATUS_GROUP }),
    { retry: false, staleTime: 5 * 60 * 1000 },
  );
  const { data: identityStatusValuesResponse, isLoading: identityStatusValuesLoading } = useQuery(
    ['DSAR_DOMAIN_VALUES', IDENTITY_STATUS_GROUP],
    () => dsarApi.getDomainValues({ group: IDENTITY_STATUS_GROUP }),
    { retry: false, staleTime: 5 * 60 * 1000 },
  );

  const queryParams = useMemo(
    () => ({
      limit: pageSize,
      offset: currentOffset,
    }),
    [currentOffset],
  );

  const {
    data: queryData,
    isLoading,
    error,
  } = useQuery(
    ['DSAR_CASES_GET_ALL', queryParams],
    async () => {
      const { data } = await dsarApi.getAllCases(queryParams);
      return data;
    },
    {
      keepPreviousData: true,
      refetchInterval: 5000,
    },
  );

  useEffect(() => {
    if (!queryData) return;
    setLoadedCases(previousCases => {
      if (currentOffset === 0) return queryData.results;
      const existingIds = new Set(previousCases.map(dsarCase => dsarCase.id));
      return [...previousCases, ...queryData.results.filter(dsarCase => !existingIds.has(dsarCase.id))];
    });
  }, [currentOffset, queryData]);

  const availableFilters = useMemo(() => {
    const statusMap = new Map<string, { id: string; name: string }>();
    const requestTypeMap = new Map<string, { id: string; name: string }>();
    const identityStatusMap = new Map<string, { id: string; name: string }>();

    loadedCases.forEach(dsarCase => {
      statusMap.set(dsarCase.status.code, { id: dsarCase.status.code, name: dsarCase.status.name });
      identityStatusMap.set(dsarCase.identityVerificationStatus.code, {
        id: dsarCase.identityVerificationStatus.code,
        name: dsarCase.identityVerificationStatus.name,
      });
      dsarCase.requestTypes.forEach(({ requestType }) => {
        requestTypeMap.set(requestType.code, { id: requestType.code, name: requestType.name });
      });
    });

    return {
      status: {
        sectionName: dsar_messages.cases.filters.status,
        filters: mapFilterItems(Array.from(statusMap.values())),
      },
      request_types: {
        sectionName: dsar_messages.cases.filters.request_types,
        filters: mapFilterItems(Array.from(requestTypeMap.values())),
      },
      identity_verification: {
        sectionName: dsar_messages.cases.filters.identity_verification,
        filters: mapFilterItems(Array.from(identityStatusMap.values())),
      },
      past_due_date: {
        sectionName: dsar_messages.cases.filters.past_due_date,
        filters: [{ id: PAST_DUE_DATE_FILTER_ID, name: t(dsar_messages.cases.filters.past_due_date_option) }],
      },
    };
  }, [loadedCases, t]);

  const tableData = useMemo(() => {
    const cases = loadedCases;
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const statusFilterIds = new Set((filters.status || []).map(filter => filter.id));
    const requestTypeFilterIds = new Set((filters.request_types || []).map(filter => filter.id));
    const identityFilterIds = new Set((filters.identity_verification || []).map(filter => filter.id));
    const pastDueDateEnabled = (filters.past_due_date || []).some(filter => filter.id === PAST_DUE_DATE_FILTER_ID);

    const filteredCases = normalizedSearch ? cases.filter(dsarCase => getSearchText(dsarCase).includes(normalizedSearch)) : cases;
    const filterMatchedCases = filteredCases.filter(dsarCase => {
      const matchesStatus = !statusFilterIds.size || statusFilterIds.has(dsarCase.status.code);
      const matchesRequestTypes =
        !requestTypeFilterIds.size || dsarCase.requestTypes.some(({ requestType }) => requestTypeFilterIds.has(requestType.code));
      const matchesIdentity = !identityFilterIds.size || identityFilterIds.has(dsarCase.identityVerificationStatus.code);
      const matchesPastDueDate = !pastDueDateEnabled || isCaseOverdue(dsarCase);

      return matchesStatus && matchesRequestTypes && matchesIdentity && matchesPastDueDate;
    });

    if (!sort.key || !sort.direction) return filterMatchedCases;

    return [...filterMatchedCases].sort((left, right) => {
      const leftValue = String(getSortValue(left, sort.key)).toLowerCase();
      const rightValue = String(getSortValue(right, sort.key)).toLowerCase();
      const result = leftValue.localeCompare(rightValue);
      return sort.direction === DIRECTIONS.DESC ? -result : result;
    });
  }, [filters, loadedCases, searchTerm, sort]);

  const handleSort = (key: string, direction: string) => {
    setSort({ key, direction });
  };

  const handleCaseSaved = (savedCase: DsarCase) => {
    setLoadedCases(previousCases => [savedCase, ...previousCases.filter(dsarCase => dsarCase.id !== savedCase.id)]);
    setCurrentOffset(0);
  };

  const handleOpenCase = (dsarCase: DsarCase) => {
    history.push(generatePath(PATHS.DSAR_CASES_DETAIL, { id: dsarCase.id }));
  };

  const handleEditCase = (dsarCase: DsarCase) => {
    history.push({
      pathname: generatePath(PATHS.DSAR_CASES_DETAIL, { id: dsarCase.id }),
      state: { openEdit: true },
    });
  };

  const hasNextPage = queryData?.next !== null && queryData?.next !== undefined;
  const domainValuesLoading = requestTypeValuesLoading || caseStatusValuesLoading || identityStatusValuesLoading;
  const requestTypeValues: DsarDomainValueSummary[] = requestTypeValuesResponse?.data || [];
  const caseStatusValues: DsarDomainValueSummary[] = caseStatusValuesResponse?.data || [];
  const identityStatusValues: DsarDomainValueSummary[] = identityStatusValuesResponse?.data || [];

  const openHelpDialog = () => {
    showHelpDialog({ title: t(dsar_messages.cases.help.title), body: t(dsar_messages.cases.help.body) });
  };

  let content: React.ReactNode;
  if (error) {
    content = <Typography color='error'>{t(general_messages.something_went_wrong)}</Typography>;
  } else if (isLoading && !queryData) {
    content = <Loader inner />;
  } else {
    content = (
      <>
        <DsarCasesTable
          data={tableData}
          filtersService={{ availableFilters, filters, setFilters }}
          loading={isLoading}
          onEdit={handleEditCase}
          onOpen={handleOpenCase}
          onSearch={setSearchTerm}
          onSort={handleSort}
          searchValue={searchTerm}
          sort={sort}
        />
        {tableData.length === 0 && !isLoading && (
          <div className={styles.emptyState}>
            <Typography variant='h6'>{t(dsar_messages.cases.empty_state.title)}</Typography>
            <Typography color='textSecondary'>{t(dsar_messages.cases.empty_state.description)}</Typography>
          </div>
        )}
        {hasNextPage && (
          <FooterButton className='' loading={isLoading} onClick={() => setCurrentOffset(prev => prev + pageSize)}>
            {t(general_messages.show_more)}
          </FooterButton>
        )}
      </>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader
          rightAdornment={
            <>
              <TextButton aria-label={t(general_messages.how_it_works)} data-testid='dsar-help-button' onClick={openHelpDialog}>
                {t(general_messages.how_it_works)}
              </TextButton>
              <ColoredButton
                aria-label={t(dsar_messages.cases.create_button)}
                className=''
                customColor='secondary'
                data-testid='dsar-create-case-button'
                disabled={false}
                icon={false}
                onClick={() => setCreateDialogOpen(true)}
                showLoader={false}
                textLike={false}
                variant='outlined'
              >
                <PlusIcon fontSize='small' />
                {t(dsar_messages.cases.create_button)}
              </ColoredButton>
            </>
          }
        >
          {t(dsar_messages.cases.page_title)}
        </ManagePageHeader>
        {content}
        <DsarCaseCreateDialog
          caseStatusValues={caseStatusValues}
          domainValuesLoading={domainValuesLoading}
          identityStatusValues={identityStatusValues}
          onClose={() => setCreateDialogOpen(false)}
          onSaved={handleCaseSaved}
          open={createDialogOpen}
          requestTypeValues={requestTypeValues}
        />
      </Container>
    </ErrorBoundary>
  );
};

export default DsarCasesPage;
